/**
 * Cross-tab login/logout broadcast — mirrors process-runner's
 * `APP_COMM_CHANNEL`. When one tab logs in/out, other tabs react.
 */

import { isValidSession } from '../session'
import { cancelRefreshToken, commenceRefreshTokenInterval } from './auth.service'

export const APP_COMM_CHANNEL = 'oro-app-comm'
export const APP_LOGIN_TRIGGER = 'APP_LOGIN_TRIGGER'
export const APP_LOGOUT_TRIGGER = 'APP_LOGOUT_TRIGGER'

const WINDOW_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

interface AppCommMessage {
  message: typeof APP_LOGIN_TRIGGER | typeof APP_LOGOUT_TRIGGER
  app: string
  window: string
}

let channel: BroadcastChannel | null = null

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  if (channel) return channel
  channel = new BroadcastChannel(APP_COMM_CHANNEL)
  return channel
}

function send(message: AppCommMessage['message']) {
  const ch = getChannel()
  if (!ch) return
  const payload: AppCommMessage = {
    message,
    app: typeof window !== 'undefined' ? window.location.hostname : '',
    window: WINDOW_ID,
  }
  ch.postMessage(payload)
}

export function broadcastLogin(): void {
  send(APP_LOGIN_TRIGGER)
}

export function broadcastLogout(): void {
  send(APP_LOGOUT_TRIGGER)
}

/**
 * Mount once on app boot. Returns an `unsubscribe` so React effects
 * can tear it down cleanly on unmount / HMR.
 */
export function initBroadcastListener(): () => void {
  const ch = getChannel()
  if (!ch) return () => undefined

  const onMessage = (e: MessageEvent<AppCommMessage>) => {
    const msg = e.data
    if (!msg || msg.window === WINDOW_ID) return
    if (msg.message === APP_LOGOUT_TRIGGER) {
      cancelRefreshToken()
      if (window.location.pathname !== '/logout' && window.location.pathname !== '/login') {
        window.location.assign('/login?reason=expired')
      }
    } else if (msg.message === APP_LOGIN_TRIGGER) {
      // Another tab signed in. If we're sitting on /login, jump to the app.
      if (isValidSession()) {
        commenceRefreshTokenInterval()
        if (window.location.pathname === '/login') {
          window.location.assign('/')
        }
      }
    }
  }
  ch.addEventListener('message', onMessage)
  return () => ch.removeEventListener('message', onMessage)
}
